import React, { useState, useEffect } from 'react';
import { superAdminAPI, companyAPI } from '../services/api';
import Icon from './Icon';

function SuperAdminMarketingTemplates({ darkMode = true }) {
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subject: '',
    html_template: '',
    assigned_company_ids: [],
    is_active: true,
    is_default: false
  });

  // Preview mode
  const [showPreview, setShowPreview] = useState(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesRes, companiesRes] = await Promise.all([
        superAdminAPI.getGlobalMarketingTemplates(),
        companyAPI.getAllCompanies()
      ]);
      setTemplates(templatesRes.data.templates || []);
      setCompanies(companiesRes.data.companies || companiesRes.data || []);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: 'Marketing Template',
      description: '',
      subject: 'Newsletter from {{company_name}}',
      html_template: getDefaultTemplate(),
      assigned_company_ids: [],
      is_active: true,
      is_default: false
    });
    setShowModal(true);
    setShowPreview(false);
  };

  const handleOpenEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      subject: template.subject || '',
      html_template: template.html_template,
      assigned_company_ids: template.assigned_company_ids || [],
      is_active: template.is_active,
      is_default: template.is_default
    });
    setShowModal(true);
    setShowPreview(false);
  };

  const handleSaveTemplate = async () => {
    if (!formData.name || !formData.html_template) {
      setError('Name and HTML template are required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (editingTemplate) {
        const response = await superAdminAPI.updateGlobalMarketingTemplate(editingTemplate.id, formData);
        const synced = response.data.synced_companies || 0;
        setSuccess(`Template updated and synced to ${synced} company(s)`);
      } else {
        await superAdminAPI.createGlobalMarketingTemplate(formData);
        setSuccess('Template created successfully!');
      }
      setShowModal(false);
      fetchData();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await superAdminAPI.deleteGlobalMarketingTemplate(templateId);
      setSuccess('Template deleted');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete template');
    }
  };

  const toggleCompany = (companyId) => {
    const ids = formData.assigned_company_ids;
    if (ids.includes(companyId)) {
      setFormData({ ...formData, assigned_company_ids: ids.filter(id => id !== companyId) });
    } else {
      setFormData({ ...formData, assigned_company_ids: [...ids, companyId] });
    }
  };

  const selectAllCompanies = () => {
    setFormData({ ...formData, assigned_company_ids: companies.map(c => c.id) });
  };

  const deselectAllCompanies = () => {
    setFormData({ ...formData, assigned_company_ids: [] });
  };

  const getCompanyName = (id) => {
    const c = companies.find(co => co.id === id);
    return c ? c.name : `Company #${id}`;
  };

  const getDefaultTemplate = () => `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        body { margin: 0; padding: 0; font-family: 'Segoe UI', Arial, sans-serif; background-color: #f4f4f4; }
        .container { max-width: 600px; margin: 0 auto; background-color: #ffffff; }
        .header { background: linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 100%); padding: 40px 30px; text-align: center; }
        .content { padding: 40px 30px; color: #333333; line-height: 1.8; }
        .highlight-box { background: linear-gradient(135deg, #f8f8f8 0%, #f0f4f0 100%); padding: 25px; margin: 25px 0; border-left: 4px solid #a3e635; border-radius: 0 12px 12px 0; }
        .cta-button { display: inline-block; padding: 16px 32px; background: linear-gradient(135deg, #a3e635 0%, #84cc16 100%); color: #0a0a1a; text-decoration: none; border-radius: 8px; margin: 20px 0; font-weight: bold; font-size: 16px; }
        .footer { background-color: #1a1a2e; padding: 30px; text-align: center; color: #a5b4fc; font-size: 0.9em; }
        .social-links { margin: 15px 0; }
        .social-links a { margin: 0 10px; text-decoration: none; color: #a3e635; }
        @media only screen and (max-width: 600px) { .content { padding: 20px 15px; } .cta-button { display: block; text-align: center; } }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1 style="color: #ffffff; margin: 0; font-size: 28px;">{{company_name}}</h1>
            <p style="color: #a5b4fc; margin: 10px 0 0 0; font-size: 14px;">Your Trusted Service Partner</p>
        </div>
        <div class="content">
            <h2 style="color: #1a1a2e; margin-top: 0;">{{email_title}}</h2>
            <p>Dear {{customer_name}},</p>
            <div class="highlight-box">
                {{email_content}}
            </div>
            <p style="margin-top: 30px;">Best regards,<br><strong>The {{company_name}} Team</strong></p>
        </div>
        <div class="footer">
            <p style="margin: 0 0 10px 0;"><strong>{{company_name}}</strong></p>
            <p style="margin: 0; font-size: 12px; color: #6b7280;">
                You're receiving this because you opted in for marketing emails.<br>
                <a href="#" style="color: #a3e635;">Manage your preferences</a>
            </p>
        </div>
    </div>
</body>
</html>`;

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>Loading...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: '0 0 6px 0', color: darkMode ? '#fff' : '#1a1a2e', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="email" color="primary" darkMode={darkMode} size={20} />
            Centralized Marketing Templates
          </h2>
          <p style={{ margin: 0, color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>
            Create templates and assign them to multiple companies. Changes sync to all assigned companies instantly.
          </p>
        </div>
        <button onClick={handleOpenCreate} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
          + New Template
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '12px', padding: '14px 18px', marginBottom: '16px', color: '#f87171', fontSize: '13px' }}>
          {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: '700' }}>x</button>
        </div>
      )}
      {success && (
        <div style={{ background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '12px', padding: '14px 18px', marginBottom: '16px', color: '#4A8C18', fontSize: '13px' }}>
          {success}
        </div>
      )}

      {/* Info Card */}
      <div style={{
        background: darkMode ? 'rgba(59, 130, 246, 0.08)' : '#eff6ff',
        border: `1px solid ${darkMode ? 'rgba(59, 130, 246, 0.15)' : '#bfdbfe'}`,
        borderRadius: '14px',
        padding: isMobile ? '16px' : '20px',
        marginBottom: '24px',
        fontSize: '13px'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: darkMode ? '#60a5fa' : '#1e40af', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Icon name="lightbulb" color="info" darkMode={darkMode} size={16} />
          How Centralized Templates Work
        </h4>
        <ul style={{ margin: 0, padding: '0 0 0 18px', color: darkMode ? 'rgba(255,255,255,0.65)' : '#374151', lineHeight: '1.8' }}>
          <li>Create a <strong>central template</strong> and assign it to one or more companies</li>
          <li>When you <strong>edit and save</strong>, the change is synced to <strong>all</strong> assigned companies automatically</li>
          <li>No need to edit templates company by company - <strong>bulk update</strong> from here</li>
          <li>Variables: <code style={{ background: darkMode ? 'rgba(255,255,255,0.1)' : '#f3f4f6', padding: '1px 5px', borderRadius: '4px', fontSize: '11px' }}>{'{{company_name}}, {{customer_name}}, {{email_title}}, {{email_content}}'}</code></li>
        </ul>
      </div>

      {/* Templates list */}
      {templates.length === 0 ? (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          background: darkMode ? 'rgba(255,255,255,0.02)' : '#fafafa',
          borderRadius: '16px',
          border: `1px dashed ${darkMode ? 'rgba(255,255,255,0.1)' : '#d1d5db'}`
        }}>
          <Icon name="email" color="secondary" darkMode={darkMode} size={40} />
          <p style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', marginTop: '16px', fontSize: '14px' }}>
            No templates yet. Create your first centralized template!
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {templates.map(template => (
            <div key={template.id} style={{
              background: darkMode ? 'rgba(255,255,255,0.03)' : '#fff',
              border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
              borderRadius: '16px',
              padding: isMobile ? '16px' : '20px',
              transition: 'border-color 0.15s'
            }}>
              {/* Template header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <strong style={{ fontSize: '16px', color: darkMode ? '#fff' : '#1f2937' }}>{template.name}</strong>
                    <span style={{
                      padding: '2px 8px', borderRadius: '8px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
                      background: template.is_active ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.12)',
                      color: template.is_active ? '#22c55e' : '#ef4444'
                    }}>
                      {template.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {template.is_default && (
                      <span style={{ padding: '2px 8px', borderRadius: '8px', fontSize: '10px', fontWeight: '700', background: 'rgba(59,130,246,0.15)', color: '#60a5fa' }}>
                        Default
                      </span>
                    )}
                  </div>
                  {template.description && (
                    <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>{template.description}</p>
                  )}
                  <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.35)' : '#9ca3af' }}>
                    Subject: {template.subject} | Updated: {template.updated_at ? new Date(template.updated_at).toLocaleString() : '-'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => handleOpenEdit(template)} style={{
                    padding: '8px 14px', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                    background: darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
                    color: darkMode ? '#fff' : '#374151'
                  }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(template.id)} style={{
                    padding: '8px 12px', border: 'none', borderRadius: '8px', fontSize: '12px', cursor: 'pointer',
                    background: 'rgba(239,68,68,0.1)', color: '#ef4444'
                  }}>
                    Delete
                  </button>
                </div>
              </div>

              {/* Assigned companies */}
              <div style={{ borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : '#f3f4f6'}`, paddingTop: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af', textTransform: 'uppercase', marginBottom: '8px' }}>
                  Assigned to ({(template.assigned_company_ids || []).length} companies)
                </div>
                {(template.assigned_company_ids || []).length === 0 ? (
                  <span style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.3)' : '#9ca3af', fontStyle: 'italic' }}>No companies assigned</span>
                ) : (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {(template.assigned_company_ids || []).map(cid => (
                      <span key={cid} style={{
                        padding: '3px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '500',
                        background: darkMode ? 'rgba(74, 140, 24, 0.15)' : 'rgba(74, 140, 24, 0.1)',
                        color: '#4A8C18'
                      }}>
                        {getCompanyName(cid)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 11000, padding: isMobile ? '8px' : '24px'
        }}>
          <div style={{
            background: darkMode ? '#1a1a3e' : '#fff', borderRadius: '20px',
            width: '95%', maxWidth: '1100px', maxHeight: '92vh', overflow: 'hidden',
            display: 'flex', flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: isMobile ? '14px 16px' : '18px 24px',
              borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: darkMode ? '#fff' : '#1f2937', fontSize: '17px' }}>
                {editingTemplate ? 'Edit Template' : 'New Centralized Template'}
              </h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  style={{
                    padding: '7px 14px', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                    background: showPreview ? 'var(--lime-primary)' : (darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb'),
                    color: showPreview ? '#0a0a1a' : (darkMode ? '#fff' : '#374151'),
                    display: 'flex', alignItems: 'center', gap: '4px'
                  }}
                >
                  {showPreview ? 'Edit' : 'Preview'}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  style={{ width: '34px', height: '34px', border: 'none', borderRadius: '8px', background: darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb', color: darkMode ? '#fff' : '#374151', fontSize: '18px', cursor: 'pointer' }}
                >x</button>
              </div>
            </div>

            {/* Modal Content */}
            <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '16px' : '24px' }}>
              {showPreview ? (
                <div style={{ maxWidth: '700px', margin: '0 auto', background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                  <div dangerouslySetInnerHTML={{
                    __html: formData.html_template
                      .replace(/\{\{company_name\}\}/g, 'Sample Company')
                      .replace(/\{\{customer_name\}\}/g, 'John Doe')
                      .replace(/\{\{email_title\}\}/g, 'Your Email Title Here')
                      .replace(/\{\{email_content\}\}/g, '<p>This is where the company\'s marketing content will appear.</p>')
                  }} />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px' }}>
                  {/* Left: Form fields */}
                  <div style={{ flex: '0 0 auto', width: isMobile ? '100%' : '340px' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151', marginBottom: '6px' }}>Template Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff', color: darkMode ? '#fff' : '#1f2937', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151', marginBottom: '6px' }}>Description</label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Optional description"
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff', color: darkMode ? '#fff' : '#1f2937', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151', marginBottom: '6px' }}>Default Subject</label>
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="Use {{company_name}} for dynamic name"
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff', color: darkMode ? '#fff' : '#1f2937', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151', cursor: 'pointer' }}>
                        <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                        Active
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151', cursor: 'pointer' }}>
                        <input type="checkbox" checked={formData.is_default} onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })} />
                        Default
                      </label>
                    </div>

                    {/* Assign companies */}
                    <div style={{
                      padding: '14px',
                      background: darkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb',
                      borderRadius: '12px',
                      border: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : '#e5e7eb'}`,
                      marginBottom: '16px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '700', color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151', textTransform: 'uppercase' }}>
                          Assign to Companies ({formData.assigned_company_ids.length}/{companies.length})
                        </label>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button type="button" onClick={selectAllCompanies} style={{ padding: '3px 8px', border: 'none', borderRadius: '6px', fontSize: '10px', fontWeight: '600', cursor: 'pointer', background: 'rgba(74,140,24,0.15)', color: '#4A8C18' }}>
                            All
                          </button>
                          <button type="button" onClick={deselectAllCompanies} style={{ padding: '3px 8px', border: 'none', borderRadius: '6px', fontSize: '10px', fontWeight: '600', cursor: 'pointer', background: darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb', color: darkMode ? '#fff' : '#374151' }}>
                            None
                          </button>
                        </div>
                      </div>
                      <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {companies.map(company => {
                          const isSelected = formData.assigned_company_ids.includes(company.id);
                          return (
                            <label key={company.id} style={{
                              display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer',
                              background: isSelected ? (darkMode ? 'rgba(74,140,24,0.12)' : 'rgba(74,140,24,0.06)') : 'transparent',
                              border: `1px solid ${isSelected ? 'rgba(74,140,24,0.2)' : 'transparent'}`,
                              transition: 'all 0.15s'
                            }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleCompany(company.id)}
                                style={{ cursor: 'pointer' }}
                              />
                              <div style={{
                                width: '24px', height: '24px', borderRadius: '6px',
                                background: 'linear-gradient(135deg, var(--lime-primary) 0%, #84cc16 100%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '12px', color: '#0a0a1a', fontWeight: '700', flexShrink: 0
                              }}>
                                {company.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <span style={{ fontSize: '13px', color: darkMode ? '#fff' : '#1f2937', fontWeight: isSelected ? '600' : '400' }}>
                                {company.name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Variables reference */}
                    <div style={{
                      padding: '12px',
                      background: darkMode ? 'rgba(245, 158, 11, 0.08)' : '#fefce8',
                      border: `1px solid ${darkMode ? 'rgba(245, 158, 11, 0.15)' : '#fef08a'}`,
                      borderRadius: '10px', fontSize: '11px',
                      color: darkMode ? '#fbbf24' : '#a16207'
                    }}>
                      <strong>Variables:</strong>
                      <div style={{ marginTop: '4px', lineHeight: '1.6' }}>
                        <code>{'{{company_name}}'}</code>, <code>{'{{customer_name}}'}</code>, <code>{'{{email_title}}'}</code>, <code>{'{{email_content}}'}</code>
                      </div>
                    </div>
                  </div>

                  {/* Right: HTML editor */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151', marginBottom: '6px' }}>
                      HTML Template *
                    </label>
                    <textarea
                      value={formData.html_template}
                      onChange={(e) => setFormData({ ...formData, html_template: e.target.value })}
                      style={{
                        width: '100%', height: isMobile ? '300px' : '500px', padding: '12px',
                        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
                        borderRadius: '10px',
                        background: darkMode ? 'rgba(0,0,0,0.3)' : '#f9fafb',
                        color: darkMode ? '#a5b4fc' : '#1f2937',
                        fontSize: '12px', fontFamily: 'Monaco, Consolas, monospace', lineHeight: '1.6',
                        resize: 'vertical', boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: isMobile ? '12px 16px' : '14px 24px',
              borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap'
            }}>
              <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af' }}>
                {formData.assigned_company_ids.length > 0
                  ? `Will sync to ${formData.assigned_company_ids.length} company(s) on save`
                  : 'No companies assigned yet'}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '10px 20px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.2)' : '#e5e7eb'}`,
                    borderRadius: '10px', background: 'transparent',
                    color: darkMode ? '#fff' : '#374151', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                  }}
                >Cancel</button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  style={{
                    padding: '10px 24px', border: 'none', borderRadius: '10px',
                    background: saving ? '#888' : 'linear-gradient(135deg, var(--lime-primary) 0%, #84cc16 100%)',
                    color: '#0a0a1a', fontSize: '13px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                >
                  {saving ? 'Saving...' : (editingTemplate ? 'Save & Sync' : 'Create Template')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminMarketingTemplates;
